import type { PropsWithChildren } from "react";
import type {
  AgentUiState,
  AgentUiViewport,
  BedrockCurrentUser,
  NotificationSoundSettings,
} from "schemas/generated/bedrock";
import type { MiniappInitialState } from "schemas/generated/miniappRpc";
import type { SelectModeParentMessage } from "schemas/generated/selectModeIframeRpc";
import type { ThemeConfig } from "schemas/generated/theme";
import type { BedrockClient } from "./client";
import type { ParentToMiniappNotificationHandlers } from "./miniappRpc";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { toast } from "@replohq/lattice/components/toast/Toast";
import * as Sentry from "@sentry/react";
import {
  focusManager,
  MutationCache,
  QueryCache,
  QueryClientProvider,
  QueryClient as TanstackQueryClient,
} from "@tanstack/react-query";
import { ReploError } from "schemas/reploError";

import {
  BedrockSessionInvalidatedError,
  getMiniappErrorToastContent,
} from "./errors";
import { getInitialMiniappRoute } from "./host-params";
import { useAgentUiParentInterface } from "./miniappRpc";
import { setMiniappTabActive } from "./miniappVisibility";
import { isRealtimeTransportError } from "./realtime-connection";
import { SelectableDetector } from "./select-mode/SelectableDetector";
import { invokeShortcut } from "./shortcutItemsRegistry";
import { restoreCommandMenuFocus, setCommandMenuState } from "./useCommandMenu";

interface AgentUiStateActions {
  setViewport: (viewport: AgentUiViewport) => void;
}

type MiniappThemeSnapshot = Parameters<
  ParentToMiniappNotificationHandlers["setThemeConfig"]
>[0];

interface BedrockContextValue {
  client: BedrockClient;
  initialMiniappRoute: string | null;
  agentUiState: AgentUiState | null;
  agentUiStateActions: AgentUiStateActions;
  currentRoute: string | null;
  // Whether this miniapp's tab is the foreground/active one. Background tabs stay
  // mounted (display:none), so use this to gate tab-scoped side effects. Defaults
  // to `true` (a standalone/non-embedded miniapp is always "active") until the
  // parent relays the real value on boot.
  isTabActive: boolean;
  // Derived from `agentUiState`; while true the agent socket is closed and live previews should unmount.
  isTabDormant: boolean;
  // Derived from `agentUiState.user`; `null` until the parent's first push.
  currentUser: BedrockCurrentUser | null;
  // Theme config the parent is currently using. `null` means the user has no
  // saved theme, so the parent renders defaults.
  themeConfig: ThemeConfig | null;
  themeSnapshot: MiniappThemeSnapshot;
  // Subscribe to editor-originated select-mode messages relayed through the
  // parent RPC. The miniapp forwards these down to its nested canopy iframe;
  // the returned function unsubscribes.
  subscribeToSelectModeParentMessage: (
    listener: (message: SelectModeParentMessage) => void,
  ) => () => void;
  // Derived from `agentUiState.notificationSoundSettings`; `null` until the
  // parent's first push.
  notificationSoundSettings: NotificationSoundSettings | null;
}

export type BedrockProviderProps = PropsWithChildren<{
  client: BedrockClient;
  initialState?: MiniappInitialState;
}>;

// TODO (Max, 2026-04-27, REPL-26671): Use createContext and useRequiredContext
// from replo-utils/react/context instead of React's createContext. Currently
// blocked by a React 18/19 type conflict between bedrock-sdk and replo-utils.
const BedrockContext = createContext<BedrockContextValue | null>(null);

class BedrockProviderMissingError extends ReploError {}

export function BedrockProvider({
  children,
  client,
  initialState,
}: BedrockProviderProps) {
  const initialMiniappRoute = useMemo(() => getInitialMiniappRoute(), []);
  const [agentUiState, setAgentUiState] = useState<AgentUiState | null>(() => {
    // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-07): Same payload the parent already validated for getInitialState.
    return initialState ? (initialState.agentUiState as never) : null;
  });
  const [currentRoute, setCurrentRoute] = useState<string | null>(
    initialState ? initialState.route : initialMiniappRoute,
  );
  const [isTabActive, setIsTabActive] = useState(
    initialState?.isTabActive ?? true,
  );
  // Mirrors `isTabActive` for the RPC handler, which must read the previous
  // value synchronously rather than through a re-rendered closure.
  const isTabActiveRef = useRef(isTabActive);
  const [themeSnapshot, setThemeSnapshot] = useState<MiniappThemeSnapshot>(
    () => ({ themeConfig: initialState?.themeConfig ?? null }),
  );
  const themeConfig = themeSnapshot.themeConfig;

  // NOTE (Max, 2026-07-31): The generated type renders `user` as `Partial<>`,
  // so re-normalize to explicit nulls before handing it to consumers.
  const currentUser = useMemo<BedrockCurrentUser | null>(() => {
    if (!agentUiState?.user) {
      return null;
    }
    return {
      name: agentUiState.user.name ?? null,
      email: agentUiState.user.email ?? null,
    };
  }, [agentUiState?.user]);
  const notificationSoundSettings: NotificationSoundSettings | null =
    agentUiState?.notificationSoundSettings ?? null;
  const isTabDormant = agentUiState?.isTabDormant ?? false;
  const [queryClient] = useState(() => {
    return new TanstackQueryClient({
      defaultOptions: {
        queries: {
          // Mini-apps render in an iframe beside the chat, so window focus flips constantly as the user moves between them; the tanstack defaults (staleTime 0...
          staleTime: 30_000,
          refetchOnWindowFocus: false,
        },
      },
      queryCache: new QueryCache({
        onError: (error, query) => {
          if (
            error instanceof BedrockSessionInvalidatedError ||
            isRealtimeTransportError(error)
          ) {
            return;
          }
          if (query.meta?.reploIgnoreDefaultErrorHandling) {
            return;
          }
          toast.error(getMiniappErrorToastContent(error));
        },
      }),
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          if (
            error instanceof BedrockSessionInvalidatedError ||
            isRealtimeTransportError(error)
          ) {
            return;
          }
          // A save that words its own failure opts out, as a read does; otherwise the person gets that message and "Request failed with status 500" beside it.
          if (mutation.meta?.reploIgnoreDefaultErrorHandling) {
            return;
          }
          toast.error(getMiniappErrorToastContent(error));
        },
      }),
    });
  });
  // NOTE (Ben, 2026-05-14): Tracks which CSS custom properties we've applied
  // to documentElement via setThemeCssVars so a subsequent update can
  // `removeProperty` any keys that dropped out of the new payload, leaving
  // inline styles in sync with the parent's intent.
  const appliedThemeCssVarsRef = React.useRef<Set<string>>(new Set());

  // Fan-out registry for relayed select-mode messages. PreviewIframe (the only
  // consumer) subscribes to forward each message into its nested canopy iframe.
  const selectModeParentMessageListenersRef = useRef<Set<
    (message: SelectModeParentMessage) => void
  > | null>(null);
  if (selectModeParentMessageListenersRef.current === null) {
    selectModeParentMessageListenersRef.current = new Set();
  }
  const subscribeToSelectModeParentMessage = useCallback(
    (listener: (message: SelectModeParentMessage) => void) => {
      selectModeParentMessageListenersRef.current?.add(listener);
      return () => {
        selectModeParentMessageListenersRef.current?.delete(listener);
      };
    },
    [],
  );

  const notificationHandlers = useMemo<ParentToMiniappNotificationHandlers>(
    () => ({
      setAgentUiState: (data) => {
        // NOTE (Gabe, 2026-09-23): a project move pushes a new workspace without reloading the iframe.
        if (data.workspaceId) {
          Sentry.setTag("workspaceId", data.workspaceId);
        }
        setAgentUiState(data);
      },
      setCurrentRoute: (data) => setCurrentRoute(data.route),
      setTabActive: (data) => {
        // Only a real background->foreground transition is a re-entry.
        const wasTabActive = isTabActiveRef.current;
        isTabActiveRef.current = data.isActive;
        setIsTabActive(data.isActive);
        setMiniappTabActive(data.isActive);
        // NOTE (Max, 2026-07-24): a miniapp tab switch is invisible to the
        // browser (no focus/visibility event reaches the iframe), so pulse
        // react-query's focus signal to refetch stale queries on re-entry.
        if (data.isActive && !wasTabActive) {
          focusManager.setFocused(true);
          focusManager.setFocused(undefined);
        }
      },
      // NOTE (Ben, 2026-05-14): Mirror the parent editor's themed CSS
      // variables onto the iframe's documentElement so mini-app pages and
      // components (which reference these vars via lattice tokens) visually
      // flow into whatever colors the active tab is using. The full set is
      // owned by the editor's paletteToCSS; we just blindly apply/remove
      // keys here so adding new tokens doesn't require an SDK change.
      setThemeCssVars: (data) => {
        const root = document.documentElement;
        const nextKeys = new Set(Object.keys(data.cssVars));
        for (const key of appliedThemeCssVarsRef.current) {
          if (!nextKeys.has(key)) {
            root.style.removeProperty(key);
          }
        }
        for (const [key, value] of Object.entries(data.cssVars)) {
          root.style.setProperty(key, value);
        }
        appliedThemeCssVarsRef.current = nextKeys;
      },
      setThemeConfig: (data) => setThemeSnapshot(data),
      setCommandMenuState,
      restoreCommandMenuFocus,
      shortcutInvoked: (data) => {
        invokeShortcut(data.itemId);
      },
      selectModeRelayMessage: (data) => {
        for (const listener of selectModeParentMessageListenersRef.current ??
          []) {
          listener(data);
        }
      },
    }),
    [queryClient],
  );

  // Replays the pulled snapshot through the notification handlers so pulled
  // and pushed state share one consumption path per channel.
  const applyInitialState = useCallback(
    (snapshot: MiniappInitialState) => {
      notificationHandlers.setCurrentRoute({ route: snapshot.route });
      notificationHandlers.setThemeCssVars({
        cssVars: snapshot.themeCssVars,
      });
      notificationHandlers.setThemeConfig({
        themeConfig: snapshot.themeConfig,
      });
      // Snapshot comes from getInitialState's validated output; generated
      // AgentUiState is slightly wider than the wire schema's z.output.
      // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-07): Same payload the parent already validated for getInitialState.
      notificationHandlers.setAgentUiState(snapshot.agentUiState as never);
      notificationHandlers.setTabActive({ isActive: snapshot.isTabActive });
    },
    [notificationHandlers],
  );

  // Theme CSS vars live on the DOM, so apply the snapshot's before first paint.
  React.useLayoutEffect(() => {
    if (initialState) {
      notificationHandlers.setThemeCssVars({
        cssVars: initialState.themeCssVars,
      });
    }
  }, [initialState, notificationHandlers]);

  const { remote } = useAgentUiParentInterface({
    notificationHandlers,
  });

  const agentUiStateActions = useMemo<AgentUiStateActions>(
    () => ({
      setViewport: (viewport) => remote.updateViewport({ viewport }),
    }),
    [remote],
  );

  // NOTE (Max, 2026-07-24): react-query v5 only watches visibilitychange,
  // which never fires when a merchant returns from a separate browser window
  // (e.g. Stripe KYC opened via window.open). Restore v4's window focus/blur
  // signal so stale queries refetch on every way back into a miniapp.
  // eslint-disable-next-line replo/no-use-effect -- Subscribes react-query's global focusManager to window events; setEventListener is the library's designed API and has no render-derivable replacement (https://tanstack.com/query/latest/docs/reference/focusManager)
  useEffect(() => {
    focusManager.setEventListener((handleFocus) => {
      const onVisibilityChange = () => {
        handleFocus(document.visibilityState === "visible");
      };
      const onWindowFocus = () => handleFocus(true);
      const onWindowBlur = () => handleFocus(false);
      window.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("focus", onWindowFocus);
      window.addEventListener("blur", onWindowBlur);
      return () => {
        window.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("blur", onWindowBlur);
        window.removeEventListener("focus", onWindowFocus);
      };
    });
    return () => {
      // NOTE (Max, 2026-07-24): restore react-query's default visibility-only
      // listener; registering it tears ours down via the closure above.
      focusManager.setEventListener((handleFocus) => {
        const onVisibilityChange = () => {
          handleFocus(document.visibilityState === "visible");
        };
        window.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
          window.removeEventListener("visibilitychange", onVisibilityChange);
        };
      });
    };
  }, []);

  // Boot handshake: pull the full state snapshot from the parent, and keep
  // the legacy ready ping for parents that only push on notifyReady.
  // eslint-disable-next-line replo/no-use-effect -- Cross-window handshake; no derivable replacement
  useEffect(() => {
    if (window.parent === window) {
      return;
    }
    remote.notifyReady();
    if (initialState) {
      return;
    }
    let cancelled = false;
    remote.getInitialState().then(
      (snapshot) => {
        if (!cancelled) {
          applyInitialState(snapshot);
        }
      },
      () => {
        // Old parents don't answer getInitialState; their notifyReady-driven
        // pushes deliver the same state.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [remote, applyInitialState, initialState]);

  // Compute socket: the SDK owns the connection to the coordinator DO.
  // RealtimeConnection mints its own token and derives the actor-scoped DO name
  // from the token's projectId/userId claims, so the provider no longer opens it
  // via useAgent; consumers read liveness through useAgentConnectionState().
  // Closed while the tab is dormant: every (re)connect warms the sandbox, so a hidden tab must hold none.
  // eslint-disable-next-line replo/no-use-effect -- Connection lifecycle bound to component mount and the tab's dormant state.
  useEffect(() => {
    // Only miniapps with a resolved identity drive the agent via client.agent: the editor
    // (browser/public auth) owns its socket via useReploAgentConnection, and connecting
    // without projectId/userId would throw in getWebsocketUrl and re-dial forever.
    if (
      client.auth.type !== "miniapp" ||
      client.auth.projectId === null ||
      client.auth.userId === null
    ) {
      return;
    }
    // Close rather than skip, so an iframe that boots dormant fails its RPCs as released, not not-ready.
    if (isTabDormant) {
      client.agent.close();
      return;
    }
    client.agent.connect();
    return () => client.agent.close();
  }, [client, isTabDormant]);

  // Collaboration socket: the project-scoped channel (ProjectCoordinator DO) —
  // project-wide `files-changed` fan-out and presence. The SDK owns it via
  // client.projectCoordinator, which mints its own token, so React and
  // non-React consumers share one path. Open for any project-bound auth
  // (miniapp, or the editor's browser auth) — presence means "has the project
  // open", so the editor holds it for the whole session (REPL-28703).
  // eslint-disable-next-line replo/no-use-effect -- Connection lifecycle bound to component mount.
  useEffect(() => {
    if (client.auth.type === "public") {
      return;
    }
    client.projectCoordinator.connect();
    return () => client.projectCoordinator.close();
  }, [client]);

  const value = useMemo(
    () => ({
      client,
      initialMiniappRoute,
      agentUiState,
      agentUiStateActions,
      currentRoute,
      isTabActive,
      isTabDormant,
      currentUser,
      themeConfig,
      themeSnapshot,
      subscribeToSelectModeParentMessage,
      notificationSoundSettings,
    }),
    [
      client,
      initialMiniappRoute,
      agentUiState,
      agentUiStateActions,
      currentRoute,
      isTabActive,
      isTabDormant,
      currentUser,
      themeConfig,
      themeSnapshot,
      subscribeToSelectModeParentMessage,
      notificationSoundSettings,
    ],
  );

  return (
    <BedrockContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>
        <SelectableDetector />
        {children}
      </QueryClientProvider>
    </BedrockContext.Provider>
  );
}

export function useBedrock(): BedrockContextValue {
  const context = useContext(BedrockContext);
  if (!context) {
    throw new BedrockProviderMissingError({
      message: "useBedrock must be used within a <BedrockProvider>",
    });
  }
  return context;
}

export function useBedrockOptional(): BedrockContextValue | null {
  return useContext(BedrockContext);
}
